import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Reveal from '../components/Reveal';
import InkThread from '../components/InkThread';
import OnTheDay from '../components/OnTheDay';
import {
  HowDefs,
  StepChoose,
  StepMake,
  StepTogether,
  TheOrganiser,
  TheMakers,
  ShipToYou,
  ShipToMakers,
  Postmark,
  PaintSpot,
} from '../components/HowDrawings';
import './HowItWorks.css';

// The whole story, read top to bottom: the idea in three steps, who the
// organiser and the makers are, how the board and canvases get to them, and
// the day itself, where it all comes together (components/OnTheDay.js) -
// with a line of ink drawing itself down the page as you scroll
// (components/InkThread.js) and ending where the day begins. The homepage
// has the short version (the steps only) and links here; the steps here are
// shorter still, so that the day itself is the payoff. The delivery options
// are postage stamps, side by side, each postmarked with where it ends up
// (only the two ways it's sent; picking it up is left to the FAQ).

const STEPS = [
  ['create', StepChoose],
  ['make', StepMake],
  ['hang', StepTogether],
];

function HowItWorks() {
  const { t } = useTranslation();

  const ways = [
    ['ship', <ShipToYou />],
    ['post', <ShipToMakers />],
  ];

  return (
    <div className="hw">
      <HowDefs />
      <InkThread />

      <header className="hw-hero hw-container">
        <PaintSpot className="hw-spot hw-spot--hero" variant="a" />
        <Reveal as="p" className="h-label">
          {t('how.kicker')}
        </Reveal>
        <Reveal as="h1" className="h-display hw-title" delay={100}>
          {t('how.title')}
        </Reveal>
      </header>

      <section className="hw-container hw-section">
        <p className="h-label">{t('how.concept.label')}</p>
        <ol className="hw-steps">
          {STEPS.map(([key, Drawing], i) => (
            <li className="hw-step" key={key}>
              <div className="hw-step-art">
                <Drawing />
              </div>
              <span className="hw-num" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h2>{t(`how.concept.steps.${key}.title`)}</h2>
              <p>{t(`how.concept.steps.${key}.text`)}</p>
            </li>
          ))}
        </ol>
      </section>

      <div className="hw-gap" aria-hidden="true" />

      <section className="hw-container hw-section">
        <PaintSpot className="hw-spot hw-spot--roles" variant="b" />
        <p className="h-label">{t('how.roles.label')}</p>
        <h2 className="h-display hw-subtitle">{t('how.roles.title')}</h2>

        <div className="hw-roles">
          <div className="hw-role">
            <TheOrganiser you={t('how.roles.organiser.you')} />
            <h3>{t('how.roles.organiser.name')}</h3>
            <p>{t('how.roles.organiser.text')}</p>
          </div>
          <div className="hw-role">
            <TheMakers />
            <h3>{t('how.roles.makers.name')}</h3>
            <p>{t('how.roles.makers.text')}</p>
          </div>
        </div>
      </section>

      <div className="hw-gap" aria-hidden="true" />

      <section className="hw-container hw-section" id="delivery">
        <PaintSpot className="hw-spot hw-spot--delivery" variant="c" />
        <p className="h-label">{t('how.delivery.label')}</p>
        <h2 className="h-display hw-subtitle">{t('how.delivery.title')}</h2>
        <p className="hw-lead">{t('how.delivery.lead')}</p>

        <ol className="hw-ways">
          {ways.map(([way, drawing]) => (
            <li className="hw-way" key={way}>
              <div className="hw-stamp-wrap">
                <div className="hw-stamp">{drawing}</div>
                <Postmark id={`pm-${way}`} place={t(`how.delivery.ways.${way}.stamp`)} />
              </div>
              <h3>{t(`how.delivery.ways.${way}.title`)}</h3>
              <p>{t(`how.delivery.ways.${way}.text`)}</p>
            </li>
          ))}
        </ol>

        <p className="hw-more">
          <Link to="/faq#delivery" className="h-link">
            {t('how.delivery.more')} <span aria-hidden="true">&rarr;</span>
          </Link>
        </p>
      </section>

      <div className="hw-gap" aria-hidden="true" />

      <OnTheDay />
    </div>
  );
}

export default HowItWorks;
